<template>
  <b-card
    no-body
    class="card-browser-states"
  >
    <b-card-header>
      <div>
        <b-card-title class="mb-0">
          {{ title }}
        </b-card-title>
        <div class="font-small-3 mb-0 text-muted">
          {{ subtitle }}
        </div>
      </div>

    </b-card-header>

    <!-- body -->
    <b-card-body>
      <div v-if="loading">
        <div class="text-center py-3">
          <b-spinner variant="primary" />
        </div>
      </div>
      <b-row v-else>
        <!-- Single Column -->
        <b-col v-if="singleColumn" cols="12">
          <div
            v-for="browser in browserData"
            :key="`all-${browser.dataIndex}-${browser.name}`"
            class="browser-states"
            @click="handleItemClick(browser)"
          >
            <b-media
              no-body
              class="align-items-center"
            >
              <b-media-aside
                v-if="!hideLogos && useIcons"
                class="mr-1"
              >
                <b-avatar
                  size="38"
                  :variant="browser.iconColor || 'light-primary'"
                  rounded
                >
                  <feather-icon
                    :icon="browser.icon"
                    size="20"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-aside
                v-else-if="!hideLogos"
                class="mr-1"
              >
                <b-img
                  :src="browser.logoUrl || browser.browserImg"
                  alt="browser img"
                />
              </b-media-aside>
              <b-media-body class="my-auto">
                <h6 class="mb-0">
                  {{ browser.name }}
                </h6>
              </b-media-body>
            </b-media>
            <div class="d-flex align-items-center">
              <span class="font-weight-bold text-body-heading mr-1">{{ browser.usage }}</span>
              <div v-if="!hideCharts" class="chart-container">
                <vue-apex-charts
                  v-if="chartData[browser.dataIndex]"
                  type="radialBar"
                  height="30"
                  width="30"
                  :options="chartData[browser.dataIndex].options"
                  :series="chartData[browser.dataIndex].series"
                />
              </div>
            </div>
          </div>
        </b-col>

        <!-- Left Column -->
        <b-col v-else cols="6">
          <div
            v-for="browser in leftColumnData"
            :key="`left-${browser.dataIndex}-${browser.name}`"
            class="browser-states"
            @click="handleItemClick(browser)"
          >
            <b-media
              no-body
              class="align-items-center"
            >
              <b-media-aside
                v-if="!hideLogos && useIcons"
                class="mr-1"
              >
                <b-avatar
                  size="38"
                  :variant="browser.iconColor || 'light-primary'"
                  rounded
                >
                  <feather-icon
                    :icon="browser.icon"
                    size="20"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-aside
                v-else-if="!hideLogos"
                class="mr-1"
              >
                <b-img
                  :src="browser.logoUrl || browser.browserImg"
                  alt="browser img"
                />
              </b-media-aside>
              <b-media-body class="my-auto">
                <h6 class="mb-0">
                  {{ browser.name }}
                </h6>
              </b-media-body>
            </b-media>
            <div class="d-flex align-items-center">
              <span class="font-weight-bold text-body-heading mr-1">{{ browser.usage }}</span>
              <div v-if="!hideCharts" class="chart-container">
                <vue-apex-charts
                  v-if="chartData[browser.dataIndex]"
                  type="radialBar"
                  height="30"
                  width="30"
                  :options="chartData[browser.dataIndex].options"
                  :series="chartData[browser.dataIndex].series"
                />
              </div>
            </div>
          </div>
        </b-col>

        <!-- Right Column -->
        <b-col v-if="!singleColumn" cols="6">
          <div
            v-for="browser in rightColumnData"
            :key="`right-${browser.dataIndex}-${browser.name}`"
            class="browser-states"
            @click="handleItemClick(browser)"
          >
            <b-media
              no-body
              class="align-items-center"
            >
              <b-media-aside
                v-if="!hideLogos && useIcons"
                class="mr-1"
              >
                <b-avatar
                  size="38"
                  :variant="browser.iconColor || 'light-primary'"
                  rounded
                >
                  <feather-icon
                    :icon="browser.icon"
                    size="20"
                  />
                </b-avatar>
              </b-media-aside>
              <b-media-aside
                v-else-if="!hideLogos"
                class="mr-1"
              >
                <b-img
                  :src="browser.logoUrl || browser.browserImg"
                  alt="browser img"
                />
              </b-media-aside>
              <b-media-body class="my-auto">
                <h6 class="mb-0">
                  {{ browser.name }}
                </h6>
              </b-media-body>
            </b-media>
            <div class="d-flex align-items-center">
              <span class="font-weight-bold text-body-heading mr-1">{{ browser.usage }}</span>
              <div v-if="!hideCharts" class="chart-container">
                <vue-apex-charts
                  v-if="chartData[browser.dataIndex]"
                  type="radialBar"
                  height="30"
                  width="30"
                  :options="chartData[browser.dataIndex].options"
                  :series="chartData[browser.dataIndex].series"
                />
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
    <!--/ body -->
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardTitle, BCardBody, BMedia, BMediaAside, BMediaBody, BImg, BAvatar, BRow, BCol, BSpinner,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { $themeColors } from '@themeConfig'
import useAppConfig from '@core/app-config/useAppConfig'
/* eslint-disable global-require */
export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BMedia,
    BMediaAside,
    BMediaBody,
    BImg,
    BAvatar,
    BRow,
    BCol,
    BSpinner,
    VueApexCharts,
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  props: {
    useIcons: {
      type: Boolean,
      default: false,
    },
    hideLogos: {
      type: Boolean,
      default: false,
    },
    hideCharts: {
      type: Boolean,
      default: false,
    },
    singleColumn: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Browser States',
    },
    subtitle: {
      type: String,
      default: 'Counter August 2020',
    },
    data: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      chartData: [],
      chartClone: {},
      chartColor: [$themeColors.primary, $themeColors.warning, $themeColors.secondary, $themeColors.info, $themeColors.danger, $themeColors.success, $themeColors.primary, $themeColors.warning, $themeColors.secondary, $themeColors.info],
      chartSeries: [54.4, 6.1, 14.6, 4.2, 8, 5.3, 3.1, 2.7, 1.5, 0.8],
      defaultBrowserData: [
        {
          browserImg: require('@/assets/images/icons/google-chrome.png'),
          name: 'Google Chrome',
          usage: '54.4%',
          icon: 'ChromeIcon',
          iconColor: 'light-primary',
          dataIndex: 0,
        },
        {
          browserImg: require('@/assets/images/icons/mozila-firefox.png'),
          name: 'Mozila Firefox',
          usage: '6.1%',
          icon: 'GlobeIcon',
          iconColor: 'light-warning',
          dataIndex: 1,
        },
        {
          browserImg: require('@/assets/images/icons/apple-safari.png'),
          name: 'Apple Safari',
          usage: '14.6%',
          icon: 'CompassIcon',
          iconColor: 'light-secondary',
          dataIndex: 2,
        },
        {
          browserImg: require('@/assets/images/icons/internet-explorer.png'),
          name: 'Internet Explorer',
          usage: '4.2%',
          icon: 'MonitorIcon',
          iconColor: 'light-info',
          dataIndex: 3,
        },
        {
          browserImg: require('@/assets/images/icons/opera.png'),
          name: 'Opera Mini',
          usage: '8.0%',
          icon: 'ZapIcon',
          iconColor: 'light-danger',
          dataIndex: 4,
        },
        {
          icon: 'ChromeIcon',
          name: 'Microsoft Edge',
          usage: '5.3%',
          iconColor: 'light-success',
          dataIndex: 5,
        },
        {
          icon: 'GlobeIcon',
          name: 'Brave Browser',
          usage: '3.1%',
          iconColor: 'light-primary',
          dataIndex: 6,
        },
        {
          icon: 'CompassIcon',
          name: 'Samsung Internet',
          usage: '2.7%',
          iconColor: 'light-warning',
          dataIndex: 7,
        },
        {
          icon: 'MonitorIcon',
          name: 'UC Browser',
          usage: '1.5%',
          iconColor: 'light-secondary',
          dataIndex: 8,
        },
        {
          icon: 'ZapIcon',
          name: 'Vivaldi',
          usage: '0.8%',
          iconColor: 'light-info',
          dataIndex: 9,
        },
      ],
      chart: {
        series: [65],
        options: {
          grid: {
            show: false,
            padding: {
              left: -15,
              right: -15,
              top: -12,
              bottom: -15,
            },
          },
          colors: [$themeColors.primary],
          plotOptions: {
            radialBar: {
              hollow: {
                size: '22%',
              },
              track: {
                background: '#ebe9f1',
              },
              dataLabels: {
                showOn: 'always',
                name: {
                  show: false,
                },
                value: {
                  show: false,
                },
              },
            },
          },
          stroke: {
            lineCap: 'round',
          },
        },
      },
    }
  },
  computed: {
    trackBgColor() {
      // Return appropriate body background color based on theme
      return this.skin === 'dark' ? '#161d31' : '#f8f8f8'
    },
    browserData() {
      if (!this.data || this.data.length === 0) {
        return this.defaultBrowserData
      }

      // Transform the data from the API into the format we need
      // Sort by numberOfProducts in descending order (create copy to avoid mutation)
      return [...this.data]
        .map((item, index) => {
          const icons = ['TrendingUpIcon', 'BarChart2Icon', 'PieChartIcon', 'ActivityIcon', 'BoxIcon', 'PackageIcon', 'LayersIcon', 'GridIcon', 'ServerIcon', 'DatabaseIcon']
          const colors = ['light-primary', 'light-warning', 'light-secondary', 'light-info', 'light-danger', 'light-success', 'light-primary', 'light-warning', 'light-secondary', 'light-info']

          // For new format with name and numberOfProducts
          if (item.name && item.numberOfProducts !== undefined) {
            return {
              browserImg: require('@/assets/images/icons/apple-safari.png'),
              logoUrl: item.logoUrl || null,
              name: item.name,
              usage: `${item.numberOfProducts}`,
              icon: item.iconname || icons[index % icons.length],
              iconColor: item.iconcolor || colors[index % colors.length],
              dataIndex: index,
              thisid: item.id,
            }
          }

          // For product types
          if (item.productType) {
            return {
              browserImg: require('@/assets/images/icons/apple-safari.png'),
              name: item.product,
              usage: `${item.numberOfProducts}`,
              icon: item.iconname || icons[index % icons.length],
              iconColor: item.iconcolor || colors[index % colors.length],
              dataIndex: index,
              filterCategory: 'producttype',
              filterValue: item.productType,
            }
          }

          // For providers
          if (item.providerType) {
            return {
              browserImg: require('@/assets/images/icons/apple-safari.png'),
              logoUrl: item.providerId ? `${this.baseURL}serveproviderlogo.cfm?id=${item.providerId}` : null,
              name: item.providerType,
              usage: `${item.numberOfProducts}`,
              icon: icons[index % icons.length],
              iconColor: colors[index % colors.length],
              dataIndex: index,
              filterCategory: 'providers',
              filterValue: item.providerType,
            }
          }

          return null
        }).filter(item => item !== null)
    },
    leftColumnData() {
      return this.browserData.filter((_, index) => index % 2 === 0)
    },
    rightColumnData() {
      return this.browserData.filter((_, index) => index % 2 !== 0)
    },
    singleColumnData() {
      return [...this.leftColumnData, ...this.rightColumnData]
    },
  },
  watch: {
    browserData: {
      handler() {
        this.$nextTick(() => {
          this.generateChartData()
        })
      },
      immediate: true,
    },
    skin: {
      handler() {
        this.$nextTick(() => {
          this.generateChartData()
        })
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.generateChartData()
    })
  },
  methods: {
    generateChartData() {
      if (!this.browserData || this.browserData.length === 0) {
        this.chartData = []
        return
      }

      const newChartData = []

      // Calculate total products for percentage
      const total = this.browserData.reduce((sum, item) => {
        const count = parseInt(item.usage.replace(/[^\d]/g, ''), 10) || 0
        return sum + count
      }, 0)

      for (let i = 0; i < this.browserData.length; i += 1) {
        const chartClone = JSON.parse(JSON.stringify(this.chart))
        chartClone.options.colors[0] = this.chartColor[i % this.chartColor.length]

        // Update track background color based on theme
        chartClone.options.plotOptions.radialBar.track.background = this.trackBgColor

        // Calculate percentage for chart series
        const count = parseInt(this.browserData[i].usage.replace(/[^\d]/g, ''), 10) || 0
        const percentage = total > 0 ? (count / total) * 100 : this.chartSeries[i] || 0
        chartClone.series[0] = percentage

        newChartData.push(chartClone)
      }

      this.chartData = newChartData
    },
    handleItemClick(item) {
      // Emit for filter-based navigation (Home page)
      if (item.filterCategory !== undefined && item.filterValue !== undefined) {
        this.$emit('item-clicked', {
          category: item.filterCategory,
          value: item.filterValue,
        })
      } else {
        // Emit the full item for distribution navigation
        this.$emit('item-clicked', item)
      }
    },
  },
}
</script>

<style scoped>
.browser-states .media-aside img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.browser-states .media-aside {
  min-width: 38px;
  min-height: 38px;
}

.chart-container {
  width: 30px;
  height: 30px;
  min-width: 30px;
  min-height: 30px;
}

.browser-states {
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 0.5rem;
  margin: 0 -0.5rem;
  border-radius: 0.357rem;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.browser-states:hover {
  background-color: rgba(26, 115, 232, 0.08);
}
</style>

<style lang="scss">
.card-browser-states .browser-states:not(:first-child) {
  margin-top: 0 !important;
}
</style>
